import { Box, Typography } from '@mui/material';
import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface CustomNodeData {
  label: string;
  isHovered: boolean;
  isUnAssigned?: boolean;
  isAdded?: boolean;
  isChild: boolean;
  isParentDisabled?: boolean;
  isChildrenDisabled?: boolean;
  type?: string;
  permissions?: string[];
}

const CustomNode = ({ data, isConnectable }: NodeProps<CustomNodeData>) => {
  const { label, isHovered, isUnAssigned, isChild, isParentDisabled, isChildrenDisabled, isAdded } =
    data;

  // Determine style based on state
  const baseStyle = {
    padding: '8px 16px',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s'
  };

  const defaultStyle = {
    backgroundColor: '#FFFFFF',
    color: '#262626',
    border: '1px solid #DADADA'
  };

  const hoveredStyle = {
    backgroundColor: ' #FFFFFF',
    color: '#262626',
    border: '2px solid #2744A0',
    boxShadow: '0px 4px 6px #6789D6'
  };

  const childStyle = {
    backgroundColor: '#FFFFFF',
    color: '#262626',
    border: '1px solid #2241A6',
    boxShadow: '0px 2px 4px #000000'
  };

  const unassignedStyle = {
    backgroundColor: ' #CAD0E4',
    color: ' #a49f9d',
    border: '1px solid #2f2e2e'
  };

  const addedStyle = {
    backgroundColor: ' #F4FFFB',
    color: ' #02B474',
    border: '1px solid #02B474'
  };

  const parentDisabledStyle = {
    backgroundColor: ' #F4F6F8',
    color: ' #ABB0BB',
    border: '1px solid #D3D6DA'
  };

  const childrenDisabledStyle = {
    backgroundColor: ' #F4FFFB',
    color: 'rgb(166, 174, 172)',
    border: '1px solid rgb(92, 165, 163)'
  };

  let nodeStyle;
  switch (true) {
    case isHovered:
      nodeStyle = hoveredStyle;
      break;
    case isUnAssigned:
      nodeStyle = unassignedStyle;
      break;
    case isAdded:
      nodeStyle = addedStyle;
      break;
    case isParentDisabled:
      nodeStyle = parentDisabledStyle;
      break;
    case isChildrenDisabled:
      nodeStyle = childrenDisabledStyle;
      break;
    case isChild:
      nodeStyle = childStyle;
      break;
    default:
      nodeStyle = defaultStyle;
  }

  return (
    <Box sx={{ ...baseStyle, ...nodeStyle, position: 'relative' }}>
      {(isAdded || isChildrenDisabled) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: ' #fff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CheckCircleIcon
            sx={{
              position: 'absolute',
              color: isAdded ? ' #27AE60' : ' #94a8a3'
            }}
          />
        </Box>
      )}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        style={{ width: 8, height: 8, backgroundColor: isChild ? '#2241A6' : '#CCCCCC' }}
      />
      <Typography sx={{ minWidth: '50px', maxWidth: '150px', textWrap: 'wrap' }}>
        {label}
      </Typography>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          backgroundColor: isHovered || isChild ? '#2241A6' : '#CCCCCC'
        }}
      />
    </Box>
  );
};

export default memo(CustomNode);
